// OrbitalX room (ORB-1086) wire types. Shapes are generated from OrbitalX.Dto.WebinarRoom — see
// api-generated.d.ts. The recording's State and Source are narrowed here because the C# DTO declares
// them `string`; the other string-valued fields have unions in types/webinarRoom.ts.

import { apiClient } from './client'
import type { ApiResponse, DownloadResult } from './client'
import type { WebViewDto } from './webs'
import type {
    WebinarRoomChatRequestDto,
    WebinarRoomConnectionDto,
    WebinarRoomHostLinkDto,
    WebinarRoomJoinDto,
    WebinarRoomMessageDto,
    WebinarRoomModerateRequestDto,
    WebinarRoomModeratorStateDto,
    WebinarRoomPollsPatchDto,
    WebinarRoomPrivateMessageRequestDto,
    WebinarRoomRecordingImportRequestDto,
    WebinarRoomRecordingPlaybackDto,
    WebinarRoomRecordingUploadDto,
    WebinarRoomRecordingUploadRequestDto,
    WebinarRoomRecordingViewDto as GeneratedWebinarRoomRecordingViewDto,
    WebinarRoomUrlDto,
    WebinarRoomWaitingVideoPlaybackRequestDto,
    WebRerunDto,
} from '@/types/api-generated'
import type { WebinarRecordingSource, WebinarRecordingState } from '@/types/webinarRoom'

export type {
    WebinarRoomActivePollDto,
    WebinarRoomAnsweringEventDto,
    WebinarRoomChatRequestDto,
    WebinarRoomConnectionDto,
    WebinarRoomDoorDto,
    WebinarRoomDoorRequestDto,
    WebinarRoomHeartbeatRequestDto,
    WebinarRoomHostLinkDto,
    WebinarRoomJoinDto,
    WebinarRoomMeDto,
    WebinarRoomMessageDto,
    WebinarRoomMessageEventDto,
    WebinarRoomMessageRemovedEventDto,
    WebinarRoomModerateRequestDto,
    WebinarRoomModeratorDto,
    WebinarRoomModeratorStateDto,
    WebinarRoomPeopleChangedEventDto,
    WebinarRoomPersonDto,
    WebinarRoomPlaybackDto,
    WebinarRoomPollAnswerRequestDto,
    WebinarRoomPollClosedEventDto,
    WebinarRoomPollInputDto,
    WebinarRoomPollLaunchedEventDto,
    WebinarRoomPollRunDto,
    WebinarRoomPollsPatchDto,
    WebinarRoomPollTallyEventDto,
    WebinarRoomPollViewDto,
    WebinarRoomPresenterDto,
    WebinarRoomPrivateMessageRequestDto,
    WebinarRoomRecordingCompleteRequestDto,
    WebinarRoomRecordingImportRequestDto,
    WebinarRoomRecordingPlaybackDto,
    WebinarRoomRecordingUploadDto,
    WebinarRoomRecordingUploadRequestDto,
    WebinarRoomSpeakerDto,
    WebinarRoomStateChangedEventDto,
    WebinarRoomSubtitleCueDto,
    WebinarRoomSubtitlesDto,
    WebinarRoomSubtitlesViewDto,
    WebinarRoomStateDto,
    WebinarRoomSummaryDto,
    WebinarRoomTokenRequestDto,
    WebinarRoomUrlDto,
    WebinarRoomWaitingImageViewDto,
    WebinarRoomWaitingVideoPlaybackRequestDto,
    WebRerunDto,
    WebRoomRecordingChangedEventDto,
} from '@/types/api-generated'

/** Blob paths and Mux ids stay server-side; `HasPlayback` says whether Mux can play it yet. */
export interface WebinarRoomRecordingViewDto
    extends Omit<GeneratedWebinarRoomRecordingViewDto, 'State' | 'Source'> {
    State: WebinarRecordingState
    Source: WebinarRecordingSource
}

/** What the Room tab sends to start an upload or import; which video it is for is the call's to say. */
type RecordingUploadRequest = Omit<WebinarRoomRecordingUploadRequestDto, 'WaitingVideo'>
type RecordingImportRequest = Omit<WebinarRoomRecordingImportRequestDto, 'WaitingVideo'>

const roomPath = (workspaceId: string, webId: string) =>
    `/api/Webs/${encodeURIComponent(workspaceId)}/${encodeURIComponent(webId)}/Room`

/**
 * Signed-in calls for the OrbitalX room (`WebinarRoomRoutes.Dashboard`), for anyone who can edit the web.
 *
 * The recording writes answer with the web, but the Room tab rereads it instead: the recording changes again
 * without this page (the worker prepares it, Mux readies it), so one read path keeps it from showing two truths.
 */
export const webinarRoomApi = {
    // Each speaker's host link and whether it was emailed or opened. Works in every stage, signed off included.
    getHostLinks: (workspaceId: string, webId: string): Promise<ApiResponse<WebinarRoomHostLinkDto[]>> =>
        apiClient.get(`${roomPath(workspaceId, webId)}/HostLinks`),

    // Emails one speaker their host link; returns that speaker's link with EmailedUtc stamped.
    emailHostLink: (workspaceId: string, webId: string, contactId: string): Promise<ApiResponse<WebinarRoomHostLinkDto>> =>
        apiClient.post(`${roomPath(workspaceId, webId)}/HostLinks/${encodeURIComponent(contactId)}/Email`, {}),

    // The viewer page with a preview token (`#p=`): a moderator watching as a viewer, never counted as attendance.
    getPreviewLink: (workspaceId: string, webId: string): Promise<ApiResponse<WebinarRoomUrlDto>> =>
        apiClient.post(`${roomPath(workspaceId, webId)}/Preview`, {}),

    // Starts a recording from the computer: a write SAS for the one blob the browser then puts the file into.
    // Replace uploads a replacement while a ready recording stays in use.
    beginRecordingUpload: (workspaceId: string, webId: string, dto: RecordingUploadRequest): Promise<ApiResponse<WebinarRoomRecordingUploadDto>> =>
        apiClient.post(`${roomPath(workspaceId, webId)}/Recording/Upload`, { ...dto, WaitingVideo: false }),

    // The browser has committed every block; the API checks the blob and starts preparing it.
    completeRecordingUpload: (workspaceId: string, webId: string, recordingId: string): Promise<ApiResponse<WebViewDto>> =>
        apiClient.post(`${roomPath(workspaceId, webId)}/Recording/${encodeURIComponent(recordingId)}/Complete`, {}),

    // A file picked in Google Drive, OneDrive or Dropbox; the worker streams it into storage.
    importRecording: (workspaceId: string, webId: string, dto: RecordingImportRequest): Promise<ApiResponse<WebViewDto>> =>
        apiClient.post(`${roomPath(workspaceId, webId)}/Recording/Import`, { ...dto, WaitingVideo: false }),

    // Prepares a recording that failed again, the one in use or a replacement.
    retryRecording: (workspaceId: string, webId: string, recordingId: string): Promise<ApiResponse<WebViewDto>> =>
        apiClient.post(`${roomPath(workspaceId, webId)}/Recording/${encodeURIComponent(recordingId)}/Retry`, {}),

    // Drops the recording on its way: the replacement, or a first upload that was cancelled.
    cancelPendingRecording: (workspaceId: string, webId: string): Promise<ApiResponse<WebViewDto>> =>
        apiClient.delete(`${roomPath(workspaceId, webId)}/Recording/Pending`),

    // The ready recording's playback, signed for this editor only: the Room tab's preview. Never counted.
    getRecordingPreview: (workspaceId: string, webId: string): Promise<ApiResponse<WebinarRoomRecordingPlaybackDto>> =>
        apiClient.get(`${roomPath(workspaceId, webId)}/Recording/Preview`),

    // The waiting video (ORB-1118) goes up through the recording's routes, waiting beside a ready one as its
    // replacement, and is completed and retried by its id like a recording.
    beginWaitingVideoUpload: (workspaceId: string, webId: string, dto: Omit<RecordingUploadRequest, 'Replace'>): Promise<ApiResponse<WebinarRoomRecordingUploadDto>> =>
        apiClient.post(`${roomPath(workspaceId, webId)}/Recording/Upload`, { ...dto, Replace: false, WaitingVideo: true }),

    importWaitingVideo: (workspaceId: string, webId: string, dto: Omit<RecordingImportRequest, 'Replace'>): Promise<ApiResponse<WebViewDto>> =>
        apiClient.post(`${roomPath(workspaceId, webId)}/Recording/Import`, { ...dto, Replace: false, WaitingVideo: true }),

    // Takes this waiting video off in any state, which is also how an upload on its way is cancelled; a newer one stays.
    removeWaitingVideo: (workspaceId: string, webId: string, videoId: string): Promise<ApiResponse<WebViewDto>> =>
        apiClient.delete(`${roomPath(workspaceId, webId)}/WaitingVideo/${encodeURIComponent(videoId)}`),

    // Once for each viewer, or on a loop until the start.
    setWaitingVideoPlayback: (workspaceId: string, webId: string, dto: WebinarRoomWaitingVideoPlaybackRequestDto): Promise<ApiResponse<WebViewDto>> =>
        apiClient.put(`${roomPath(workspaceId, webId)}/WaitingVideo/Playback`, dto),

    // The ready waiting video's playback, signed for this editor only: the waiting screen's preview.
    getWaitingVideoPreview: (workspaceId: string, webId: string): Promise<ApiResponse<WebinarRoomRecordingPlaybackDto>> =>
        apiClient.get(`${roomPath(workspaceId, webId)}/WaitingVideo/Preview`),

    // The image shown instead of a waiting video (multipart, one field `file`), in place of any image before it.
    // Refused while a waiting video is set. Answers with the web.
    uploadWaitingImage: (workspaceId: string, webId: string, file: File): Promise<ApiResponse<WebViewDto>> => {
        const formData = new FormData()
        formData.append('file', file)
        return apiClient.post(`${roomPath(workspaceId, webId)}/WaitingImage`, formData)
    },

    removeWaitingImage: (workspaceId: string, webId: string): Promise<ApiResponse<WebViewDto>> =>
        apiClient.delete(`${roomPath(workspaceId, webId)}/WaitingImage`),

    // The ready recording's subtitles (ORB-1132): one SRT or VTT file (multipart field `file`) and the spoken language
    // (JSON field `request`), in place of any before them. The Room tab shows a refusal in place, so the global handler
    // stays quiet on its 422. Answers with the web.
    uploadRecordingSubtitles: (workspaceId: string, webId: string, recordingId: string, file: File, languageCode: string): Promise<ApiResponse<WebViewDto>> => {
        const formData = new FormData()
        formData.append('request', JSON.stringify({ LanguageCode: languageCode }))
        formData.append('file', file)
        return apiClient.post(`${roomPath(workspaceId, webId)}/Recording/${encodeURIComponent(recordingId)}/Subtitles`, formData, { showsOwnValidationErrors: true })
    },

    removeRecordingSubtitles: (workspaceId: string, webId: string, recordingId: string): Promise<ApiResponse<WebViewDto>> =>
        apiClient.delete(`${roomPath(workspaceId, webId)}/Recording/${encodeURIComponent(recordingId)}/Subtitles`),

    // Replaces the web's whole poll list; a poll left out is deleted. Answers with the web.
    savePolls: (workspaceId: string, webId: string, dto: WebinarRoomPollsPatchDto): Promise<ApiResponse<WebViewDto>> =>
        apiClient.put(`${roomPath(workspaceId, webId)}/Polls`, dto),

    // Every chat message and question, as CSV.
    downloadTranscript: (workspaceId: string, webId: string): Promise<DownloadResult> =>
        apiClient.download(`${roomPath(workspaceId, webId)}/Transcript`),

    // The moderator room's bootstrap: the room, the web's polls and who the signed-in user is in it.
    getModeratorJoin: (workspaceId: string, webId: string): Promise<ApiResponse<WebinarRoomJoinDto>> =>
        apiClient.get(`${roomPath(workspaceId, webId)}/Moderator`),

    // A fresh `webinar` hub token for the moderator, asked for again on every reconnect.
    negotiate: (workspaceId: string, webId: string): Promise<ApiResponse<WebinarRoomConnectionDto>> =>
        apiClient.post(`${roomPath(workspaceId, webId)}/Negotiate`, {}),

    // Every message and question, hidden ones included, as moderators see them.
    getHistory: (workspaceId: string, webId: string): Promise<ApiResponse<WebinarRoomMessageDto[]>> =>
        apiClient.get(`${roomPath(workspaceId, webId)}/History`),

    // People, presenters, moderators and poll runs.
    getModeratorState: (workspaceId: string, webId: string): Promise<ApiResponse<WebinarRoomModeratorStateDto>> =>
        apiClient.get(`${roomPath(workspaceId, webId)}/State`),

    // Posts in the chat as host.
    postChat: (workspaceId: string, webId: string, dto: WebinarRoomChatRequestDto): Promise<ApiResponse<WebinarRoomMessageDto>> =>
        apiClient.post(`${roomPath(workspaceId, webId)}/Chat`, dto),

    // Writes privately to one registrant: only they and those running the room see it.
    postPrivateMessage: (workspaceId: string, webId: string, dto: WebinarRoomPrivateMessageRequestDto): Promise<ApiResponse<WebinarRoomMessageDto>> =>
        apiClient.post(`${roomPath(workspaceId, webId)}/PrivateMessage`, dto),

    moderate: (workspaceId: string, webId: string, dto: WebinarRoomModerateRequestDto): Promise<ApiResponse<unknown>> =>
        apiClient.post(`${roomPath(workspaceId, webId)}/Moderate`, dto),

    // Re-run (ORB-1065): a new web in the same campaign for a new date, with this one's length. Answers 201 with it.
    rerun: (workspaceId: string, campaignId: string, webId: string, dto: WebRerunDto): Promise<ApiResponse<WebViewDto>> =>
        apiClient.post(`/api/Webs/${encodeURIComponent(workspaceId)}/${encodeURIComponent(campaignId)}/${encodeURIComponent(webId)}/Rerun`, dto),
}
